import React, { useContext } from 'react';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import classNames from 'classnames';
import {
  ISection,
  ISuggestion,
} from '../../../../../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import EDTooltip from '../../../../../../../../../../BaseComponents/Tooltip/EDTooltip';
import EDTypography from '../../../../../../../../../../BaseComponents/Typography/EDTypography';
import EkonomiskAnalysSelectAnalysList from '../EkonomiskAnalysSelectAnalysList/EkonomiskAnalysSelectAnalysList';
import { ISelectedAnalys } from '../../../../../EkonomiskAnalysPage';
import EDIcon from '../../../../../../../../../../BaseComponents/Icon/EDIcon';
import EDCollapse from '../../../../../../../../../../BaseComponents/Collapsables/Collapse/EDCollapse';
import { EkonomiskAnalysSideMenuContext } from '../../../Context/EkonomiskAnalysSideMenuContext';
import { IRecentAnalysInfo } from '../../AddToRecentAnalyserMutation/AddToRecentAnalyserMutation';

import { EKAnalysGroupingModeType } from '../FavoriteAnalysDialog/EkonomiskAnalysFavoriteAnalysDialog';
import { IAnalysSuggestion } from '../FavoriteAnalysDialog/Utils/EkonomiskAnalysFavoriteAnalysDialogUtils';
import { ANALYSKATEGORI_ID_VISA_SAMMANFATTNING } from '../../../../../Const/EkonomiskAnalysConsts';

interface IProps {
  selectedAnalys?: ISelectedAnalys | null;
  sections: ISection[];
  sortMode?: EKAnalysGroupingModeType;
  openCodePrivileges: string[];
  setOpenCodePrivilege: (codePrivilege: string | null) => () => void;
  searchValue: string;
  onAnalysSelected: (
    suggestion: ISuggestion,
    onClearSearch: () => void,
    section: ISuggestion,
    sammanfattningsMode: boolean
  ) => void;
  renderAnalysListItem?: (
    analys: ISuggestion,
    section: ISection,
    middleComponent: React.ReactNode
  ) => React.ReactNode;
  renderSectionHeader?: (section: ISection) => React.ReactNode;
  renderRecentSection?: boolean;
  classes?: any;
  sammanfattningsMode: boolean;
}

interface IInnerProps extends IProps {}

const styles = (theme: Theme) => ({
  container: {
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(5),
  },
  itemContainer: {
    paddingTop: theme.spacing(2),
  },
  headerContainer: {
    display: 'flex',
    flex: '0 1 auto',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cursor: {
    cursor: 'pointer',
  },
  listHeader: {
    textOverflow: 'ellipsis',
    width: 200,
    overflow: 'hidden',
  },
});

export const recentAnalyserIdName = 'recentAnalyser';
export const attKvitteraIdName = 'attKvittera';

const recentAnalyserToSection = (
  recentAnalyser: IRecentAnalysInfo[]
): ISection => {
  return {
    name: 'Senaste analyser',
    value: recentAnalyserIdName,
    items: recentAnalyserToSuggestions(recentAnalyser),
  };
};

const recentAnalyserToSuggestions = (
  recentAnalyser: IRecentAnalysInfo[]
): ISuggestion[] => {
  return recentAnalyser.map<ISuggestion>(analys => {
    const data: IAnalysSuggestion = {
      id: analys.analysId,
      idkod: analys.analysIdKod,
      name: analys.analysName,
      baseSelection: {
        id: analys.baseSelectionId,
        name: analys.baseSelectionName,
      },
      favoritLopnr: null,
      utdelatLopnr: analys.utdelningLopnr,
      kategori: analys.category || 'Senaste analyser',
      kategori_ID: analys.categoryID || undefined,
      kodBehName: analys.kodbehName,
      kodBehId: analys.kodbehId,
      obligatorisk: false,
      obligatoriskRoller: [],
      kodBehKombinerad: false,
      ai_prompt: analys.ai_prompt ?? null,
      detaljanalys: Boolean(analys.detaljanalys),
    };

    return {
      name: analys.analysName,
      value: analys.analysId,
      data,
    };
  });
};

const EkonomiskAnalysSelectListBase: React.FunctionComponent<
  React.PropsWithChildren<IInnerProps>
> = props => {
  const { analysSelectListRef, userInfo } = useContext(
    EkonomiskAnalysSideMenuContext
  );

  const onSelected =
    (suggestion: ISuggestion, section: ISuggestion) =>
    (event: React.MouseEvent<any>) => {
      props.onAnalysSelected(
        suggestion,
        () => {},
        {
          name: suggestion.data.kodBehName,
          value: suggestion.data.kodBehId,
        },
        suggestion?.data?.kategori_ID === ANALYSKATEGORI_ID_VISA_SAMMANFATTNING
      );
    };

  const filterAnalysList = (section: ISection, visibleReports?: ISection[]) => {
    const { searchValue } = props;

    return section.items.filter(
      analys =>
        analys.name
          .toLowerCase()
          .trim()
          .includes(searchValue.toLowerCase().trim()) ||
        section.name
          .toLowerCase()
          .trim()
          .includes(searchValue.toLowerCase().trim())
    );
  };

  if (!props.sections) {
    return null;
  }
  const { classes } = props;

  const recentAnalyser: ISection | null = userInfo
    ? recentAnalyserToSection(userInfo.recentAnalyser || [])
    : null;

  const renderRecent =
    props.renderRecentSection &&
    recentAnalyser &&
    filterAnalysList(recentAnalyser).length > 0;

  return (
    <div className={classes.container} ref={analysSelectListRef}>
      {renderRecent && (
        <div className={classes.itemContainer} key={recentAnalyserIdName}>
          <div
            onClick={props.setOpenCodePrivilege(recentAnalyserIdName)}
            className={classNames(classes.cursor, classes.headerContainer)}
            data-testid={recentAnalyserIdName}
          >
            <EDTypography variant="body2" color="textSecondary" bold>
              Senaste analyser
            </EDTypography>
            <EDIcon
              marginRight={1}
              paddingRight={2}
              iconType={
                props.openCodePrivileges.includes(recentAnalyserIdName)
                  ? 'arrow_drop_up'
                  : 'arrow_drop_down'
              }
            />
          </div>
          <EDCollapse
            unmountOnExit
            mountOnEnter
            open={props.openCodePrivileges.includes(recentAnalyserIdName)}
          >
            <EkonomiskAnalysSelectAnalysList
              renderListItem={props.renderAnalysListItem}
              searchValue={props.searchValue}
              section={recentAnalyserToSection(
                userInfo && userInfo.recentAnalyser
                  ? userInfo.recentAnalyser
                  : []
              )}
              selectedAnalys={props.selectedAnalys}
              analyser={filterAnalysList(
                recentAnalyserToSection(
                  userInfo && userInfo.recentAnalyser
                    ? userInfo.recentAnalyser
                    : []
                ),
                props.sections
              )}
              onAnalysSelected={onSelected}
              groupingMode="category"
            />
          </EDCollapse>
        </div>
      )}

      {props.sections.map((section, sectionsIndex) => {
        const open = props.openCodePrivileges.includes(section.value);

        return (
          <div className={classes.itemContainer} key={sectionsIndex}>
            <div
              onClick={props.setOpenCodePrivilege(section.value)}
              className={classNames(classes.cursor, classes.headerContainer)}
              data-testid={section.name}
            >
              {props.renderSectionHeader && props.renderSectionHeader(section)}
              {!props.renderSectionHeader && (
                <EDTooltip title={section.name}>
                  <EDTypography
                    variant="body2"
                    color="textSecondary"
                    className={classes.listHeader}
                    bold
                  >
                    {section.name}
                  </EDTypography>
                </EDTooltip>
              )}
              <EDIcon
                marginRight={1}
                paddingRight={2}
                iconType={open ? 'arrow_drop_up' : 'arrow_drop_down'}
              />
            </div>
            <EDCollapse unmountOnExit mountOnEnter open={open}>
              <EkonomiskAnalysSelectAnalysList
                renderListItem={props.renderAnalysListItem}
                searchValue={props.searchValue}
                section={section}
                selectedAnalys={props.selectedAnalys}
                analyser={filterAnalysList(section)}
                onAnalysSelected={onSelected}
                groupingMode={
                  props.sortMode
                    ? props.sortMode
                    : userInfo && userInfo.analysGroupingMode
                    ? userInfo.analysGroupingMode.value
                    : 'kodbeh'
                }
              />
            </EDCollapse>
          </div>
        );
      })}
    </div>
  );
};

const EkonomiskAnalysSelectList: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  return <EkonomiskAnalysSelectListBase {...props} />;
};

export default withStyles(EkonomiskAnalysSelectList, styles);
